import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Heart, Sparkles, Users, Baby, Leaf, Quote } from "lucide-react";
import { Layout } from "../components/Layout";
import { LotusMark } from "../components/Logo";
import { siteConfig } from "../data/siteConfig";
import { programs } from "../data/programs";

const highlights = [
  {
    icon: Baby,
    title: "Prenatal & Garbh Sanskar",
    text: "Nurturing, trimester-wise yoga for conception, pregnancy and beyond.",
  },
  {
    icon: Sparkles,
    title: "Kids & Teens Yoga",
    text: "Playful, certified sessions that build focus, confidence and calm.",
  },
  {
    icon: Users,
    title: "Family & Corporate",
    text: "Practice together at home, or bring wellness to your workplace.",
  },
  {
    icon: Leaf,
    title: "Personalised 1-on-1",
    text: "Private sessions tailored to your body, goals and schedule.",
  },
];

const testimonials = [
  {
    quote:
      "Suman's prenatal classes kept me calm and strong through all three trimesters. I felt cared for at every step.",
    name: "Priya M.",
    role: "Prenatal student",
  },
  {
    quote:
      "My kids actually ask for their yoga class now! It's playful but they've genuinely become calmer and more focused.",
    name: "Anita R.",
    role: "Parent, Kids Yoga",
  },
  {
    quote:
      "Our team's online corporate sessions are the highlight of the week — less stress, better energy, real difference.",
    name: "David L.",
    role: "Corporate client, Hong Kong",
  },
];

export const IndexPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-28 md:pt-32 pb-20 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-accent/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-6 relative grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-6">
              <LotusMark className="w-4 h-4" /> {siteConfig.subTagline}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold text-charcoal leading-[1.05] mb-6">
              Find harmony in
              <span className="block text-primary italic">
                body, mind &amp; breath
              </span>
            </h1>
            <p className="text-lg text-charcoal/70 leading-relaxed max-w-xl mb-8">
              {siteConfig.tagline} — bringing the timeless practice of yoga to
              individuals, families and organisations, online and across Hong
              Kong &amp; India.
            </p>
            <div className="flex flex-wrap gap-4">
              <RouterLink
                to="/programs"
                className="px-7 py-3.5 bg-primary text-white rounded-full font-semibold shadow-soft hover:bg-primary-dark hover:-translate-y-0.5 transition-all"
              >
                Explore Programs
              </RouterLink>
              <RouterLink
                to="/contact"
                className="px-7 py-3.5 bg-white text-primary border border-accent/40 rounded-full font-semibold hover:bg-cream-dark transition-all"
              >
                Book a Class
              </RouterLink>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-[2.5rem] overflow-hidden shadow-soft-lg border-4 border-white">
              <img
                src="/gallery/hero-waterfront.jpg"
                alt="Suman Agrawal practising yoga by the Hong Kong waterfront"
                className="w-full h-[28rem] object-cover object-center"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-soft px-5 py-4 hidden sm:block">
              <p className="font-heading text-3xl text-primary font-semibold">
                5+ yrs
              </p>
              <p className="text-xs text-charcoal/60 uppercase tracking-widest">
                Teaching · India &amp; HK
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-cream-dark">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <LotusMark className="w-10 h-10 mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl text-charcoal mb-5">
            Yoga as a way of life
          </h2>
          <p className="text-charcoal/70 text-lg leading-relaxed">
            Led by Suman Agrawal — Chartered Accountant turned yoga instructor,
            trained at Rikhiapeeth and affiliated with the Bihar School of Yoga —
            every session creates harmony between body, mind and emotions, for
            individuals and groups alike.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl md:text-4xl text-charcoal mb-3">
              What we offer
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto">
              Thoughtfully designed yoga for every stage and every body.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h) => (
              <div
                key={h.title}
                className="bg-white rounded-3xl p-7 border border-accent/15 shadow-soft hover:-translate-y-1 transition-transform"
              >
                <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <h.icon className="w-6 h-6" />
                </span>
                <h3 className="font-heading text-xl text-charcoal mb-2">
                  {h.title}
                </h3>
                <p className="text-sm text-charcoal/65 leading-relaxed">
                  {h.text}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <RouterLink
              to="/programs"
              className="inline-block px-7 py-3.5 bg-primary text-white rounded-full font-semibold shadow-soft hover:bg-primary-dark transition-all"
            >
              See all {programs.length} programs
            </RouterLink>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-charcoal text-cream">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-3">
              Loved by our community
            </h2>
            <p className="text-cream/70">
              A few words from students &amp; families.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="bg-white/10 rounded-3xl p-7 backdrop-blur-sm"
              >
                <Quote className="w-8 h-8 text-accent-light mb-4" />
                <p className="text-cream/90 leading-relaxed mb-6">"{t.quote}"</p>
                <p className="font-semibold text-white">{t.name}</p>
                <p className="text-xs text-cream/60 uppercase tracking-widest">
                  {t.role}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="bg-white rounded-[2.5rem] shadow-soft-lg border border-accent/15 p-10 md:p-16 text-center max-w-4xl mx-auto">
            <Heart className="w-10 h-10 text-primary mx-auto mb-5" />
            <h2 className="font-heading text-3xl md:text-4xl text-charcoal mb-4">
              Begin your yoga journey today
            </h2>
            <p className="text-charcoal/70 mb-8 max-w-xl mx-auto">
              Whether you're expecting, new to yoga, or looking to deepen your
              practice — there's a class waiting for you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <RouterLink
                to="/contact"
                className="px-7 py-3.5 bg-primary text-white rounded-full font-semibold shadow-soft hover:bg-primary-dark transition-all"
              >
                Get in touch
              </RouterLink>
              <RouterLink
                to="/pricing"
                className="px-7 py-3.5 bg-white text-primary border border-accent/40 rounded-full font-semibold hover:bg-cream-dark transition-all"
              >
                View pricing
              </RouterLink>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IndexPage;
